/**
 * フッター会話管理
 *
 * RPG風の会話ボックスを制御するMixin。
 * シーンごとに複数ページの会話テキストを表示し、クリックでページ送りする。
 *
 * 操作するDOM要素:
 *   footerDialogueTextAreaTarget       - 会話テキスト表示エリア
 *   footerDialogueTextCursorUpTarget   - 上カーソル（前ページあり表示）
 *   footerDialogueTextCursorDownTarget - 下カーソル（次ページあり表示）
 *
 * 使用するStimulus values:
 *   footerDialoguePageTextsValue   - 各ページの会話テキスト配列 例: [["行1", "行2"], ["行1"]]
 *   currentFooterDialoguePageIndexValue  - 現在のページインデックス 例: 0, 1, 2
 */
import { buildNotificationDialogueTexts, buildFooterDialogueTexts } from "../data/footer-dialogue-text";
import { Scenes, MS_PER_CHAR } from "../constants";

export const FooterDialogueManagementMixin = {
  // ============================================
  // エントリーポイント
  // ============================================

  /**
   * シーン切り替え時に会話テキストを更新
   * scene-navigation.jsの各switchToScene*メソッドから呼ばれる
   * @param {string} [notification] - 通知タイプ（指定時はシーン会話テキストの代わりに通知を表示）
   */
  updateFooterDialogueText(notification) {
    if (!this.hasFooterDialogueTextAreaTarget) return;

    // 通知がある場合はシーン会話テキストの代わりに通知を表示
    if (notification) {
      const notificationTexts = buildNotificationDialogueTexts(notification);
      if (notificationTexts) {
        this.showFooterDialogue(notificationTexts);
        return;
      }
      // 不明な通知タイプの場合はシーン会話テキストにフォールバック
    }

    const scene = this.currentSceneValue;
    const hasInput = scene === Scenes.PAYOUT_REQUEST_TOP
      && this.hasPayoutRequestInputTarget
      && this.payoutRequestInputTarget.value.length > 0;

    // 入力ありの場合、フラグを設定（onChangeAmountでの再アニメーション防止用）
    if (hasInput) {
      this.payoutInputDialogueShown = true;
    }
    // 出金申請画面から離れたらフラグをリセット
    if (scene !== Scenes.PAYOUT_REQUEST_TOP) {
      this.payoutInputDialogueShown = false;
    }

    const pageTexts = this.getFooterDialogueTexts(scene, hasInput);
    this.showFooterDialogue(pageTexts);
  },

  // ============================================
  // ヘルパー
  // ============================================

  /**
   * シーンに対応する会話テキスト配列を取得
   * @param {string} scene - Scenes定数の値
   * @param {boolean} hasInput - 出金申請画面で入力があるかどうか
   */
  getFooterDialogueTexts(scene, hasInput) {
    const data = {
      userAmount: this.payoutRequestAmountValue.toLocaleString(),
      hasInput,
    };

    return buildFooterDialogueTexts(scene, data);
  },

  /**
   * 会話テキストを設定して表示
   * @param {string[][]} pageTexts - 会話テキスト配列（ページ × 行）
   */
  showFooterDialogue(pageTexts) {
    // 同じテキストが既に表示中の場合は再アニメーションをスキップ
    if (JSON.stringify(pageTexts) === JSON.stringify(this.footerDialoguePageTextsValue)) {
      return;
    }

    this.footerDialoguePageTextsValue = pageTexts;
    this.currentFooterDialoguePageIndexValue = 0;
    this.showCurrentFooterDialoguePage();
  },

  // ============================================
  // レンダリング
  // ============================================

  /**
   * 現在のページを表示し、カーソルの表示/非表示を更新
   * 各行をspan要素として追加し、CSS幅アニメーションで徐々に表示する
   */
  showCurrentFooterDialoguePage() {
    if (!this.hasFooterDialogueTextAreaTarget) return;

    const pageTexts = this.footerDialoguePageTextsValue || [];
    const pageIndex = this.currentFooterDialoguePageIndexValue;
    const lines = pageTexts[pageIndex] || [];

    // 次ページがあれば下カーソル表示、前ページがあれば上カーソル表示
    const cursorDownDisplay = pageIndex < pageTexts.length - 1 ? "block" : "none";
    const cursorUpDisplay = pageIndex > 0 ? "block" : "none";

    // テキストエリアをクリア
    this.footerDialogueTextAreaTarget.replaceChildren();

    // アニメーション開始
    this.isFooterDialogueAnimating = true;

    let cumulativeChars = 0;
    let totalDuration = 0;

    lines.forEach((lineText) => {
      // span要素を生成
      const span = document.createElement("span");
      span.textContent = lineText;
      span.classList.add("harvest-warehouse-footer__text-line");

      // DOMに追加して自然幅を計測
      this.footerDialogueTextAreaTarget.appendChild(span);
      const naturalWidth = span.scrollWidth;

      // 初期状態: 幅0
      span.style.width = "0px";

      // この行のアニメーション時間と遅延を計算
      const duration = lineText.length * MS_PER_CHAR;
      const delay = cumulativeChars * MS_PER_CHAR;

      span.style.transition = `width ${duration}ms linear ${delay}ms`;

      // 累積文字数を更新
      cumulativeChars += lineText.length;
      totalDuration = delay + duration;

      // requestAnimationFrame × 2 でブラウザに初期状態を確定させてからアニメーション開始
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          span.style.width = `${naturalWidth}px`;
        });
      });
    });

    // 全行のアニメーション完了後にフラグを解除
    setTimeout(() => {
      this.isFooterDialogueAnimating = false;
    }, totalDuration);

    // カーソル表示を更新
    if (this.hasFooterDialogueTextCursorDownTarget) {
      this.footerDialogueTextCursorDownTarget.style.display = cursorDownDisplay;
    }

    if (this.hasFooterDialogueTextCursorUpTarget) {
      this.footerDialogueTextCursorUpTarget.style.display = cursorUpDisplay;
    }
  },

  // ============================================
  // ユーザー操作（ページ送り）
  // ============================================

  /**
   * 会話ボックスクリック時のページ送り処理
   * 上半分クリック → 前ページ、下半分クリック → 次ページ
   * アニメーション中はクリックを無視する
   */
  handleFooterDialogueClick(event) {
    if (this.isFooterDialogueAnimating) return;

    const pageTexts = this.footerDialoguePageTextsValue;
    const pageIndex = this.currentFooterDialoguePageIndexValue;

    // box: クリックされた会話ボックス要素
    const box = event.currentTarget;

    // clickedUpperHalf: クリック位置が要素の上半分かどうか
    // offsetYはクリック位置の要素内での相対Y座標
    const clickedUpperHalf = event.offsetY < box.offsetHeight / 2;

    if (clickedUpperHalf) {
      if (pageIndex > 0) {
        this.previousFooterDialogue();
      }
    } else {
      if (pageIndex < pageTexts.length - 1) {
        this.nextFooterDialogue();
      }
    }
  },

  /**
   * 次ページへ進む
   */
  nextFooterDialogue() {
    const pageTexts = this.footerDialoguePageTextsValue;
    const pageIndex = this.currentFooterDialoguePageIndexValue;

    if (pageIndex < pageTexts.length - 1) {
      this.currentFooterDialoguePageIndexValue += 1;
      this.showCurrentFooterDialoguePage();
    }
  },

  /**
   * 前ページへ戻る
   */
  previousFooterDialogue() {
    const pageIndex = this.currentFooterDialoguePageIndexValue;

    if (pageIndex > 0) {
      this.currentFooterDialoguePageIndexValue -= 1;
      this.showCurrentFooterDialoguePage();
    }
  },
};
